import { useState, useEffect } from 'react';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';

const useImpersonation = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check impersonation status on mount
  useEffect(() => {
    checkImpersonationStatus();
  }, []);

  const checkImpersonationStatus = async () => {
    try {
      const response = await axios.get(createApiUrl('/impersonation/status'), {
        withCredentials: true
      });
      
      if (response.data.success) {
        const isImpersonating = response.data.isImpersonating || (response.data.user && response.data.user.isImpersonated);
        setIsImpersonating(isImpersonating);
        setImpersonationData(response.data);
      }
    } catch (error) {
      console.error('Error checking impersonation status:', error);
    }
  };

  const startImpersonation = async (targetUserId, targetUserType) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(createApiUrl('/impersonation/start'), {
        targetUserId,
        targetUserType
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setIsImpersonating(true);
        setImpersonationData(response.data);
        
        // Redirect to the appropriate dashboard based on user type
        const user = response.data.user;
        const origin = window.location.origin;
        if (user && user.isImpersonated) {
          if (user.userType === 'organization' && user.isOrgAdmin) {
            window.location.href = `${origin}/org-admin-dashboard`;
          } else if (user.userType === 'schoolAdmin' && user.isSchAdmin) {
            window.location.href = `${origin}/school-admin-dashboard`;
          } else if (user.userType === 'student' || user.role === 'student') {
            window.location.href = `${origin}/student-dashboard`;
          } else {
            window.location.href = `${origin}/teacher-dashboard`;
          }
        } else {
          window.location.reload();
        }
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.message || 'Failed to start impersonation');
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to start impersonation';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const stopImpersonation = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(createApiUrl('/impersonation/stop'), {}, {
        withCredentials: true
      });

      if (response.data.success) {
        setIsImpersonating(false);
        setImpersonationData(null);
        
        // Redirect to admin dashboard
        window.location.href = '/admin-dashboard';
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.message || 'Failed to stop impersonation');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to stop impersonation';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getUsersForImpersonation = async (searchQuery = '', userType = 'organization', page = 1, limit = 20) => {
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        userType,
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await axios.get(createApiUrl(`/impersonation/users?${params}`), {
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching users for impersonation:', error);
      throw error;
    }
  };

  return {
    isImpersonating,
    impersonationData,
    loading,
    error,
    startImpersonation,
    stopImpersonation,
    checkImpersonationStatus,
    getUsersForImpersonation
  };
};

export default useImpersonation;